import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Star } from "lucide-react";
import { api } from "../services/api";
import { useWishlistActions } from "../hooks/useWishlistActions";
import { productToCartItem, writeCartItem } from "../utils/checkoutCart";
import { productDetailImages, resolveProductImage } from "../utils/productAssets";
import { renderHtml } from "../utils/renderHtml";
import { getDisplaySoldCount } from "../utils/displaySoldCount";
import { formatUsDate } from "../utils/timezone";

function useToast() {
  const [toast, setToast] = useState({ visible: false, message: "" });
  const showToast = (message) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: "" }), 2200);
  };
  return { toast, showToast };
}

function normalizeVariantAttributesObject(variant = {}) {
  const rawAttributes = variant.attributes;
  const attributes = {};

  if (rawAttributes && typeof rawAttributes === "object") {
    if (rawAttributes instanceof Map) {
      Object.assign(attributes, Object.fromEntries(rawAttributes.entries()));
    } else {
      Object.entries(rawAttributes).forEach(([name, value]) => {
        const cleanName = String(name || "").trim();
        const cleanValue = String(value || "").trim();
        if (cleanName && cleanValue) attributes[cleanName] = cleanValue;
      });
    }
  }

  if (!Object.keys(attributes).length && variant.optionName && variant.optionValue) {
    attributes[String(variant.optionName).trim()] = String(variant.optionValue).trim();
  }

  return attributes;
}

function normalizeOptionText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function getVariantAttributeValue(variant, attributeName) {
  const attributes = normalizeVariantAttributesObject(variant);
  const exactValue = attributes[attributeName];
  if (exactValue) return exactValue;

  const normalizedName = String(attributeName || "").trim().toLowerCase();
  const match = Object.entries(attributes).find(([name]) => String(name || "").trim().toLowerCase() === normalizedName);
  return match?.[1] || "";
}

function variantMatchesSelection(variant, attributes, selectedOptions) {
  return attributes.every((attribute) => {
    const selectedValue = selectedOptions[attribute.name];
    if (!selectedValue) return false;
    return normalizeOptionText(getVariantAttributeValue(variant, attribute.name)) === normalizeOptionText(selectedValue);
  });
}

function Productdetails({ adminPreview = false }) {
  const navigate = useNavigate();
  const { productId, productKey } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState({ averageRating: 0, count: 0, reviews: [] });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const { toast, showToast } = useToast();
  const { isSaved, loading: wishlistLoading, toggleWishlist } = useWishlistActions(product, showToast);

  useEffect(() => {
    const key = productKey || "new-tension-complete-singer-20u-zig-zag-s20u-tension-comp";
    let mounted = true;
    const requestPath = adminPreview ? `/products/${productId}/preview` : `/listings/${key}`;

    api.get(requestPath)
      .then((data) => {
        if (mounted) setProduct(data.product);
      })
      .catch(() => {
        if (mounted) setProduct(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [adminPreview, productId, productKey]);

  useEffect(() => {
    if (!product?._id && !product?.sku) return undefined;
    let mounted = true;
    setReviewsLoading(true);
    const id = encodeURIComponent(product._id || product.sku);
    const sku = product.sku ? `?sku=${encodeURIComponent(product.sku)}` : "";
    api.get(`/reviews/product/${id}${sku}`)
      .then((data) => {
        if (mounted) {
          setReviewData({
            averageRating: data.averageRating || 0,
            count: data.count || 0,
            reviews: data.reviews || [],
          });
        }
      })
      .catch(() => {
        if (mounted) setReviewData({ averageRating: 0, count: 0, reviews: [] });
      })
      .finally(() => {
        if (mounted) setReviewsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [product]);

  const variationAttributes = useMemo(() => {
    const explicit = Array.isArray(product?.variantAttributes) ? product.variantAttributes : [];
    if (explicit.length) return explicit.filter((attribute) => attribute?.variation !== false && attribute.values?.length);

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const map = new Map();
    variants.forEach((variant) => {
      Object.entries(normalizeVariantAttributesObject(variant)).forEach(([name, value]) => {
        if (!name || !value) return;
        if (!map.has(name)) map.set(name, new Set());
        map.get(name).add(value);
      });
    });

    return Array.from(map.entries()).map(([name, values]) => ({
      name,
      values: Array.from(values),
      visible: true,
      variation: true,
    }));
  }, [product]);
  const enabledVariants = useMemo(() => (product?.variants || []).filter((variant) => variant.enabled !== false), [product]);
  const hasVariations = variationAttributes.length > 0;
  const selectedVariant = useMemo(() => {
    if (!hasVariations) return null;
    return enabledVariants.find((variant) => variantMatchesSelection(variant, variationAttributes, selectedOptions)) || null;
  }, [enabledVariants, hasVariations, selectedOptions, variationAttributes]);
  const displayProduct = useMemo(() => {
    if (!selectedVariant) return product;

    const variantPrice = Number(selectedVariant.price || 0);
    const variantCompareAtPrice = Number(selectedVariant.compareAtPrice || 0);

    return {
      ...product,
      sku: selectedVariant.sku || product.sku,
      price: variantPrice > 0 ? variantPrice : product.price,
      compareAtPrice: variantCompareAtPrice > 0 ? variantCompareAtPrice : product.compareAtPrice,
      stock: selectedVariant.stock ?? product.stock,
      image: selectedVariant.image || product.image,
      weightLb: selectedVariant.weightLb ?? product.weightLb,
    };
  }, [product, selectedVariant]);
  const images = useMemo(() => productDetailImages(displayProduct), [displayProduct]);
  const maxQty = Math.max(1, Math.min(Number(displayProduct?.stock || 1), 99));
  const allOptionsSelected = !hasVariations || variationAttributes.every((attribute) => selectedOptions[attribute.name]);
  const canPurchase = Number(displayProduct?.stock || 0) > 0 && allOptionsSelected && (!enabledVariants.length || selectedVariant);
  const displaySoldCount = getDisplaySoldCount(product);

  useEffect(() => {
    if (!product) return;
    const defaults = {};
    (product.variantAttributes || []).forEach((attribute) => {
      if (attribute.values?.length === 1) defaults[attribute.name] = attribute.values[0];
    });
    setSelectedOptions(defaults);
  }, [product]);

  useEffect(() => {
    setActiveImg(0);
  }, [selectedVariant?._id, selectedVariant?.image]);

  const goTo = (index) => setActiveImg((index + images.length) % images.length);
  const handleBuyNow = () => {
    if (!canPurchase) {
      showToast(hasVariations ? "Please choose all product options" : "This product is out of stock");
      return;
    }
    writeCartItem(productToCartItem(product, qty, selectedVariant, selectedOptions));
    navigate("/checkout");
  };
  const handleAddCart = () => {
    if (!canPurchase) {
      showToast(hasVariations ? "Please choose all product options" : "This product is out of stock");
      return;
    }
    writeCartItem(productToCartItem(product, qty, selectedVariant, selectedOptions));
    showToast(`${qty} item${qty > 1 ? "s" : ""} added to cart`);
    setTimeout(() => navigate("/cart"), 350);
  };
  const handleShare = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(window.location.href);
    showToast("Link copied to clipboard");
  };

  if (loading) {
    return <main className="mx-auto max-w-7xl px-6 py-20 text-sm font-semibold text-gray-500">Loading product...</main>;
  }

  if (!product) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-20">
        <h1 className="text-3xl font-black text-gray-900">Product not found</h1>
        <Link className="mt-4 inline-block rounded-xl bg-[#3991D5] px-5 py-3 text-sm font-bold text-white" to="/all-products">
          Back to products
        </Link>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans text-black antialiased">
      <div className={`fixed bottom-7 left-1/2 z-50 -translate-x-1/2 rounded-full bg-gray-900 px-6 py-3 text-xs font-bold text-white transition-all duration-300 ${toast.visible ? "opacity-100" : "pointer-events-none translate-y-10 opacity-0"}`}>
        {toast.message}
      </div>
      {adminPreview && (
        <div className="fixed right-0 left-0 m-auto w-[100px] text-center top-[180px] z-50 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-black uppercase tracking-wide text-amber-800 shadow-lg">
          Preview
        </div>
      )}

      <main className="mx-auto max-w-7xl px-6 py-12 md:py-20">
        <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-12">
          <section className="space-y-4 lg:col-span-7">
            <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-3xl border bg-white p-8 shadow-lg">
              <div className="absolute left-6 top-6 z-10">
                <span className="rounded-full bg-white/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest backdrop-blur-md">
                  {displayProduct.stock > 0 ? `${displayProduct.stock} available` : "Out of stock"}
                </span>
              </div>
              <button className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-xl font-bold shadow-md" onClick={() => goTo(activeImg - 1)} type="button">
                &lt;
              </button>
              <img alt={product.name} className="h-full w-full object-contain" src={images[activeImg] || resolveProductImage(product.image)} />
              <button className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-xl font-bold shadow-md" onClick={() => goTo(activeImg + 1)} type="button">
                &gt;
              </button>
            </div>

            <div className="flex justify-center gap-4 overflow-x-auto py-2">
              {images.map((src, index) => (
                <button
                  key={src}
                  onClick={() => goTo(index)}
                  className={`h-20 w-20 flex-shrink-0 rounded-2xl bg-white p-2 shadow ${index === activeImg ? "border-2 border-sky-400" : "border border-gray-200"}`}
                  type="button"
                >
                  <img alt={`${product.name} ${index + 1}`} className="h-full w-full object-contain" src={src} />
                </button>
              ))}
            </div>
          </section>

          <section className="flex flex-col gap-8 lg:col-span-5">
            <div className="space-y-5">
              <p className="text-xs font-black uppercase tracking-widest text-sky-500">{product.brandLine} / {product.category}</p>
              <h1 className="text-4xl font-bold leading-[1.1] tracking-tight">{product.name}</h1>
              <p className="text-sm leading-6 text-gray-500" dangerouslySetInnerHTML={renderHtml(product.shortDescription)} />
            <div className="rounded-3xl bg-white/70 p-4 shadow">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold">Bobbinusa.com</p>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-sky-500">99.6% positive</p>
                </div>
                  <div className="text-right">
                    <span className="block rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-sky-600">SKU {displayProduct.sku}</span>
                    <p className="mt-2 text-[11px] font-bold text-gray-400">
                      {Number(product.reviewCount || reviewData.count || 0)} review{Number(product.reviewCount || reviewData.count || 0) === 1 ? "" : "s"}
                      {` · ${displaySoldCount} sold`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2 border-y py-5">
              <div className="flex items-baseline gap-3">
                <span className="text-4xl font-black">US ${Number(displayProduct.price || 0).toFixed(2)}</span>
                {displayProduct.compareAtPrice && <span className="text-lg font-bold text-gray-400 line-through">${Number(displayProduct.compareAtPrice).toFixed(2)}</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-gray-500">
                <span>{reviewData.count ? `${reviewData.averageRating.toFixed(1)} rating` : "No ratings yet"}</span>
                <span>•</span>
                <span>{Number(product.reviewCount || reviewData.count || 0)} review{Number(product.reviewCount || reviewData.count || 0) === 1 ? "" : "s"}</span>
                <span>•</span>
                <span>{displaySoldCount} sold</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-widest">Condition:</span>
                <span className="text-xs font-bold">{product.condition || "New"}</span>
              </div>
            </div>

            <div className="space-y-4">
              {hasVariations && (
                <div className="space-y-4 rounded-3xl border border-gray-100 bg-white/70 p-5 shadow">
                  {variationAttributes.map((attribute) => (
                    <div key={attribute.name}>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <span className="text-xs font-bold uppercase tracking-widest">{attribute.name}</span>
                        {selectedOptions[attribute.name] && (
                          <span className="text-xs font-bold text-sky-500">{selectedOptions[attribute.name]}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(attribute.values || []).map((value) => {
                          const selected = selectedOptions[attribute.name] === value;

                          return (
                            <button
                              className={`rounded-2xl border px-4 py-2 text-sm font-black transition ${
                                selected
                                  ? "border-sky-300 bg-sky-50 text-sky-600"
                                  : "border-gray-200 bg-white text-gray-700 hover:border-sky-300"
                              }`}
                              key={value}
                              onClick={() => {
                                setSelectedOptions((current) => ({ ...current, [attribute.name]: value }));
                                setQty(1);
                              }}
                              type="button"
                            >
                              {value}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {allOptionsSelected && !selectedVariant && (
                    <p className="rounded-2xl bg-amber-50 px-4 py-3 text-xs font-bold text-amber-700">
                      This option combination is not available.
                    </p>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between rounded-3xl bg-white/70 px-6 py-4 shadow">
                <span className="text-xs font-bold uppercase tracking-widest">Quantity</span>
                <div className="flex items-center gap-6">
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-xl font-bold disabled:text-gray-300" disabled={qty <= 1} onClick={() => setQty((current) => Math.max(1, current - 1))} type="button">-</button>
                  <span className="min-w-7 text-center text-lg font-bold">{qty}</span>
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-xl font-bold disabled:text-gray-300" disabled={qty >= maxQty} onClick={() => setQty((current) => Math.min(maxQty, current + 1))} type="button">+</button>
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <button className="w-full rounded-3xl bg-sky-300 py-5 text-sm font-bold uppercase tracking-widest shadow transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50" disabled={!canPurchase} onClick={handleBuyNow} type="button">
                  Buy It Now
                </button>
                <button className="w-full rounded-3xl bg-white/70 py-5 text-sm font-bold uppercase tracking-widest shadow transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50" disabled={!canPurchase} onClick={handleAddCart} type="button">
                  Add to Cart
                </button>
                <button className={`w-full py-2 text-xs font-bold uppercase tracking-widest ${isSaved ? "text-red-500" : "hover:text-sky-400"}`} disabled={wishlistLoading} onClick={toggleWishlist} type="button">
                  {isSaved ? "Saved to Wishlist" : "Add to Wishlist"}
                </button>
              </div>
            </div>

            <div className="space-y-4 border-t pt-6">
              <div className="rounded-3xl bg-white/70 p-6 shadow">
                <div className="flex justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest">Shipping</span>
                  <span className="text-xs font-bold text-sky-500">USPS Ground Free</span>
                </div>
                <p className="mt-3 text-[13px] leading-relaxed text-gray-600">
                  Canada and air service options are calculated at checkout from admin shipping rules.
                </p>
              </div>
              <button className="rounded-full bg-white/70 px-8 py-3 text-xs font-bold uppercase tracking-widest shadow" onClick={handleShare} type="button">
                Share Product
              </button>
            </div>
          </section>
        </div>
        <p className="achormark text-sm leading-6 text-gray-500" dangerouslySetInnerHTML={renderHtml(product.description)} />
        <section className="mt-14 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-4 border-b border-gray-100 pb-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-sky-500">Customer reviews</p>
              <h2 className="mt-2 text-3xl font-black text-gray-950">Product reviews</h2>
              <p className="mt-2 text-sm font-semibold text-gray-500">
                Reviews are shown after verified delivery from customer orders.
              </p>
            </div>
            <div className="rounded-2xl bg-blue-50 px-5 py-4 text-right">
              <div className="flex items-center justify-end gap-1 text-amber-500">
                <ReviewStars rating={reviewData.averageRating} />
              </div>
              <p className="mt-1 text-sm font-black text-gray-900">
                {reviewData.count ? `${reviewData.averageRating.toFixed(1)} from ${reviewData.count} review${reviewData.count === 1 ? "" : "s"}` : "No reviews yet"}
              </p>
            </div>
          </div>

          {reviewsLoading ? (
            <p className="py-8 text-sm font-bold text-gray-500">Loading reviews...</p>
          ) : reviewData.reviews.length ? (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {reviewData.reviews.map((review) => (
                <article className="rounded-2xl border border-gray-100 bg-gray-50 p-5" key={review._id}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-black text-gray-900">{review.user?.name || "Verified customer"}</p>
                      <p className="mt-1 text-xs font-bold uppercase tracking-widest text-gray-400">
                        {formatUsDate(review.createdAt)}
                      </p>
                    </div>
                    <div className="flex text-amber-500"><ReviewStars rating={review.rating} /></div>
                  </div>
                  <p className="mt-4 text-sm font-semibold leading-6 text-gray-600">{review.comment}</p>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl bg-gray-50 p-6 text-sm font-semibold text-gray-500">
              No customer reviews yet. Delivered customers can review this item from My Account.
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function ReviewStars({ rating }) {
  const rounded = Math.round(Number(rating || 0));
  return [1, 2, 3, 4, 5].map((star) => (
    <Star className="h-4 w-4" fill={rounded >= star ? "currentColor" : "none"} key={star} />
  ));
}

export default Productdetails;
